import { PROJECT_REGION_MAP } from './regionData'
import { ENRICHED_MOCK_RISK_AREAS } from './mockPreventionData'
import { FactorSeverity } from '../types/prevention'

export interface ActivityExample {
  activityName: string
  projectName: string
  currentRisk: 'critical' | 'high' | 'medium'
  riskAfter: 'high' | 'medium' | 'low'
  contractor: string
  floor: string
  explanation: string
}

export interface Recommendation {
  id: string
  title: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  rationale: string
  expectedImpact: string
  affectedProjects: string[]
  linkedDrivers: string[]
  scope: 'systemic' | 'region' | 'project'
  regionId?: string
  riskReduction: number
  projectsAffected?: number
  contractorsAffected?: number
  activityExamples: ActivityExample[]
}

// Seeded PRNG for stable generation
class PRNG {
  private seed: number
  constructor(seed: number) {
    this.seed = seed
  }
  next() {
    this.seed = (this.seed * 16807) % 2147483647
    return (this.seed - 1) / 2147483646
  }
  nextInt(min: number, max: number) {
    return Math.floor(this.next() * (max - min + 1)) + min
  }
  pick<T>(arr: T[]): T {
    return arr[this.nextInt(0, arr.length - 1)]
  }
  shuffle<T>(arr: T[]): T[] {
    const result = [...arr]
    for (let i = result.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i)
      ;[result[i], result[j]] = [result[j], result[i]]
    }
    return result
  }
}

const rng = new PRNG(12345)

const TOPICS = [
  {
    title:
      'Re-establish edge protection and PFAS tie-off discipline on upper-floor slab perimeters',
    rationale:
      'The system flagged 6 fall-protection observations in the last 7 days on floors 18-22, including two crews working within 6 feet of unprotected edges after guardrail sections were removed for material landing. The issue is clustering around afternoon concrete patchback and layout work, and the exposure aligns with 29 CFR 1926.501 and 1926.502 requirements for unprotected sides and edges.',
    expectedImpact:
      'Addresses the leading fatal event in construction; BLS recorded 389 construction deaths from falls, slips, and trips in 2024, and OSHA continues to anchor its fall campaign around this hazard.',
    drivers: ['Observation Severity', 'Open Observation Count'],
    activities: [
      'Slab Edge Formwork Strip-Out',
      'Embed Layout at Floor Perimeter',
      'Concrete Patch and Grind at Deck Edge',
    ],
    contractors: [
      'Empire City Concrete',
      'Hudson Valley Interiors',
      'Keystone Layout Services',
    ],
  },
  {
    title:
      'Standardize ladder access and housekeeping in shafts, stairs, and above-ceiling rough-in zones',
    rationale:
      'Nine ladder-use observations were logged in the last 14 days, and 41% of the electrical and mechanical rough-in manpower on the affected floors has been on site less than 30 days. Portable ladders are being used as work platforms in stair towers and above-ceiling zones, which points to a training-and-access issue under 29 CFR 1926.1053 rather than a one-off housekeeping miss.',
    expectedImpact:
      'Targets a persistent precursor to falls and strains during interior build-out, especially when crews are turning over quickly and workfronts change floor by floor.',
    drivers: ['New Worker Ratio', 'Observation Frequency'],
    activities: [
      'Stair Tower Conduit Rough-In',
      'Above-Ceiling Duct Branch Installation',
      'Elevator Lobby Drywall Framing',
    ],
    contractors: [
      'Blue Ridge Electric Systems',
      'Capital Air Balance & Sheet Metal',
      'Monument Interiors',
    ],
  },
  {
    title:
      'Tighten supported scaffold inspections and front-edge protection before facade and masonry work starts',
    rationale:
      'Three required scaffold inspections were missed this week, and 5 observations documented incomplete decking or excessive platform-to-face gaps after scaffold moves on the south elevation. The pattern is repeating after reconfiguration, so the recommendation ties directly to 29 CFR 1926.451 access and platform requirements and 1926.454 training expectations.',
    expectedImpact:
      'Expected to reduce exposure during exterior access work where small setup defects can quickly become serious fall or dropped-object events.',
    drivers: ['Inspection Compliance', 'Reinspection Rate'],
    activities: [
      'Brick Veneer Repair from Supported Scaffold',
      'Facade Sealant Replacement',
      'Exterior Air Barrier Punch List',
    ],
    contractors: [
      'Tri-State Masonry Restoration',
      'Atlantic Facade Access',
      'Metro Waterproofing Group',
    ],
  },
  {
    title:
      'Reinforce leading-edge fall planning for steel and deck crews before weekly fly days',
    rationale:
      'The project logged 4 high-severity observations during beam landing and metal deck leading-edge work over the last two crane cycles, and only 3 of 8 related PTPs identified anchor locations and rescue steps. CPWR fall research links inadequate planning to lower use of fall protection, making this a planning-and-execution issue as much as a PPE issue under 29 CFR 1926.760 and 1926.761.',
    expectedImpact:
      'Addresses a high-consequence phase where one missed anchor point or sequencing error can produce a fatal event and a major erection stoppage.',
    drivers: ['PTP Quality', 'Project Phase'],
    activities: [
      'Fly Structural Steel Beams',
      'Install Composite Metal Deck',
      'Set Perimeter Cable at Leading Edge',
    ],
    contractors: [
      'Northeast Steel Erectors',
      'Allegheny Decking Systems',
      'River City Crane Service',
    ],
  },
  {
    title:
      'Require full-time drop-zone control and tool tethering for overhead facade and hoistway work',
    rationale:
      'Seven struck-by observations were recorded in 10 days below curtain wall loading bays and open hoistway edges, including unsecured tools, loose banding, and workers crossing under active lift zones. The trend is strongest during stacked trades on podium levels and points to weak drop-zone control, overhead protection, and exclusion-zone discipline rather than an isolated housekeeping issue.',
    expectedImpact:
      'Expected to reduce one of the most common severe-injury pathways on dense commercial sites: workers below active lifting or overhead hand work.',
    drivers: ['Site Congestion', 'Observation Severity'],
    activities: [
      'Unitized Curtain Wall Panel Installation',
      'Hoist Mast Tie-In Work',
      'Fireproofing at Perimeter Beams',
    ],
    contractors: [
      'Pacific Rim Glazing',
      'Vertical Access Solutions',
      'Summit Fireproofing',
    ],
  },
  {
    title:
      'Separate pedestrians from telehandlers, loaders, and delivery trucks with hard barricades and spotter rules',
    rationale:
      'Heavy equipment density has climbed sharply since podium turnover, and 12 pedestrian-equipment interaction observations were logged near loading docks and laydown alleys in the last two weeks. CPWR struck-by survey findings emphasize equipment proximity and schedule pressure as common barriers, so this needs physical separation and disciplined routing rather than signage alone.',
    expectedImpact:
      'Addresses a major serious-injury precursor in construction logistics zones, especially on mixed-use projects with simultaneous structure, enclosure, and site work.',
    drivers: ['Heavy Equipment Density', 'Site Congestion'],
    activities: [
      'Material Offload at Loading Dock',
      'Telehandler Distribution to Floor Openings',
      'Dumpster Swap and Haul Route Operations',
    ],
    contractors: [
      'Mid-Atlantic Site Logistics',
      'Pioneer Forklift Services',
      'Harbor Trucking & Disposal',
    ],
  },
  {
    title:
      'Revalidate rigging plans and red-zone controls for prefab MEP racks and long picks',
    rationale:
      'Two near misses and 5 medium-to-high severity observations this month involved unstable loads, improvised tag-line use, and workers entering the fall zone during prefab rack picks. OSHA requires shift inspection of rigging under 29 CFR 1926.251, and suspended-load controls under 29 CFR 1926.1425 need to be visible in the field, not just in the lift plan.',
    expectedImpact:
      'Expected to improve control reliability during long, blind, or high-value picks where one rigging failure can trigger struck-by, caught-between, and schedule impacts at once.',
    drivers: ['Near-Miss Ratio', 'Controls Validation'],
    activities: [
      'Prefabricated MEP Rack Setting',
      'Air Handler Equipment Hoist',
      'Generator Skid Placement',
    ],
    contractors: [
      'Great Lakes Rigging',
      'Precision Mechanical Assemblies',
      'Urban Crane & Hoist',
    ],
  },
  {
    title:
      'Re-check crane work zones and power-line clearances before utility-side picks',
    rationale:
      'The site generated 3 recent observations of boom or load travel near temporary utility corridors, and one lift plan relied on outdated site sketches after a feeder reroute. For cranes, 29 CFR 1926.1408 requires evaluating whether equipment, load line, or load can breach minimum approach distances to power lines before the pick begins.',
    expectedImpact:
      'Addresses a low-frequency but catastrophic exposure with life-safety, outage, and major project continuity consequences.',
    drivers: ['Controls Validation', 'Project Type'],
    activities: [
      'Tower Crane Assisted Rebar Cage Pick',
      'Generator Yard Equipment Placement',
      'Site Transformer Setting',
    ],
    contractors: [
      'Sunbelt Crane Services',
      'Highline Electrical Constructors',
      'Delta Utility Installers',
    ],
  },
  {
    title:
      'Enforce temporary power inspections, GFCI coverage, and cord management at every active floor',
    rationale:
      'Weekly inspections found 11 damaged-cord or spider-box conditions across three levels, and 4 observations involved cords passing through wet-cutting or housekeeping debris zones. OSHA 29 CFR 1926.404 requires GFCI protection or an assured equipment grounding conductor program on construction sites, and 29 CFR 1926.416 requires verifying energized hazards before work begins.',
    expectedImpact:
      'Expected to reduce both shock exposure and nuisance outages that interrupt interior build-out and energization activities.',
    drivers: ['Inspection Compliance', 'Open Observation Count'],
    activities: [
      'Temporary Power Distribution Setup',
      'Core Drill Water Management',
      'Interior Lighting String Installation',
    ],
    contractors: [
      'Blue Ridge Electric Systems',
      'Metro Core Drilling',
      'Beacon Interiors',
    ],
  },
  {
    title:
      'Tighten trench protective-system checks and spoil placement for utility excavations',
    rationale:
      'Competent-person documentation is inconsistent on 5 of the last 9 trench entries, and inspectors recorded spoil piles and pipe bundles too close to the trench lip on two consecutive utility runs. OSHA Subpart P, including 29 CFR 1926.651 and 1926.652, requires daily inspections and protective systems where cave-in hazards exist.',
    expectedImpact:
      "Addresses one of construction's highest-consequence caught-in/between events, where collapses can be immediately fatal and trigger stop-work across civil scopes.",
    drivers: ['Inspection Compliance', 'Controls Validation'],
    activities: [
      'Storm Line Trench Excavation',
      'Domestic Water Service Tie-In',
      'Electrical Ductbank Installation',
    ],
    contractors: [
      'Frontier Earthworks',
      'Carolina Utility Services',
      'Mesa Trench Support',
    ],
  },
  {
    title:
      'Mandate Table 1 silica controls and visible dust checks during interior concrete cutting',
    rationale:
      'Dust-related observations have doubled since dry core drilling and tuck-point grinding began on the amenity floors, and 3 of 7 recent PTPs for those tasks did not specify water delivery or HEPA capture. Under 29 CFR 1926.1153, crews need either the prescribed Table 1 control method or exposure assessment with controls that hold exposure below the PEL.',
    expectedImpact:
      'Expected to reduce a common health exposure that can also create housekeeping and trade-interference issues in enclosed build-out zones.',
    drivers: ['Hazardous Materials', 'PTP Quality'],
    activities: [
      'Wall Saw Opening Creation',
      'Core Drilling for Sleeve Penetrations',
      'Surface Prep by Handheld Grinder',
    ],
    contractors: [
      'Metro Core Drilling',
      'Precision Demo Sawcut',
      'Liberty Concrete Rehab',
    ],
  },
  {
    title:
      'Launch regional heat acclimatization and work-rest-water triggers before summer pours and roofing',
    rationale:
      'Weather severity has moved into the top decile for the portfolio, and the project recorded back-to-back fatigue and dizziness first-aid cases during afternoon deck pours and membrane staging. OSHA updated its heat National Emphasis Program on April 10, 2026, and CPWR notes that one in three heat-related fatal injuries in 2023 occurred in construction, making acclimatization and supervisor triggers timely rather than seasonal paperwork.',
    expectedImpact:
      'Expected to improve reliability on hot-weather workdays while reducing escalation from early symptoms to medical cases, delayed pours, or emergency response.',
    drivers: ['Weather Severity', 'Fatigue Risk'],
    activities: [
      'Elevated Slab Placement',
      'Roof Membrane Installation',
      'Exterior Site Concrete Finishing',
    ],
    contractors: [
      'Sunbelt Concrete Placers',
      'Gulf Coast Roofing',
      'Desert Finishers',
    ],
  },
  {
    title:
      'Require pre-demolition hazardous-material verification before renovation and tie-in work',
    rationale:
      'Two recent demo scopes started with generic JHAs even though drawings flagged legacy fireproofing, mastic, and painted steel in the affected areas, and sampled workers could not identify the regulated area limits or respiratory requirements. OSHA 29 CFR 1926.1101 for asbestos and 29 CFR 1926.62 for lead both require exposure-aware planning, and the current gap is the handoff between survey results, material information, permits, and the crews entering the space.',
    expectedImpact:
      'Addresses high-liability health exposures that can create worker harm, contamination events, and lengthy area shutdowns if discovered late.',
    drivers: ['Hazardous Materials', 'SDS Compliance & Engagement'],
    activities: [
      'Selective Interior Demolition',
      'Pipe Insulation Removal',
      'Steel Surface Prep at Renovation Tie-In',
    ],
    contractors: [
      'Heritage Abatement Group',
      'Great Lakes Demo',
      'Liberty Industrial Coatings',
    ],
  },
  {
    title:
      'Start noise monitoring and hearing-protection enforcement for demolition and deck fastening crews',
    rationale:
      'Observation notes show repeated work around powder-actuated fastening, chipping hammers, and sawcutting where crews are speaking at shout level, but PPE validation is inconsistent between shifts. OSHA 29 CFR 1926.52 sets construction noise exposure requirements, and the fastest way to stabilize this risk is task-based monitoring paired with supervisor enforcement.',
    expectedImpact:
      'Expected to reduce preventable hearing-loss exposure and improve communication discipline on high-noise operations where misheard signals can compound other hazards.',
    drivers: ['PPE Validation', 'Observation Frequency'],
    activities: [
      'Selective Concrete Demolition',
      'Metal Deck Fastening',
      'Asphalt Sawcut for Site Tie-In',
    ],
    contractors: [
      'Great Lakes Demo',
      'River City Decking',
      'Southwest Paving Sawcut',
    ],
  },
  {
    title:
      'Verify crane pad, outrigger, and assembly/disassembly controls before each reconfiguration',
    rationale:
      'The crane fleet has changed configuration three times in 14 days, but only one ground-bearing calculation was attached to the last two assembly packages and inspectors noted outrigger mats staged late. Subpart CC places responsibility on the employer to ensure ground conditions and assembly/disassembly controls are addressed before operation.',
    expectedImpact:
      'Expected to reduce low-frequency, high-severity crane events and avoid major schedule disruption during superstructure and enclosure turnover.',
    drivers: ['Project Phase', 'Inspection Compliance'],
    activities: [
      'Crawler Crane Assembly',
      'Mobile Crane Repositioning',
      'Perimeter Hoist Base Relocation',
    ],
    contractors: [
      'Sunbelt Crane Services',
      'Solid Ground Shoring',
      'Iron Range Erectors',
    ],
  },
  {
    title:
      'Assign qualified riggers and signal-person verification to blind picks and dual-trade lifts',
    rationale:
      'Six lift plans this month involved blind landings into mechanical rooms or shafts, but only half documented the signal person and load-path restrictions. On dense commercial sites, rigging discipline breaks down fastest during repetitive interior picks, so crew qualification needs to be as visible as the equipment inspection.',
    expectedImpact:
      'Improves lift consistency on complex interior and shaft picks where miscommunication often precedes struck-by and crush injuries.',
    drivers: ['Worker Competency', 'Controls Validation'],
    activities: [
      'Chiller Tube Bundle Rig-In',
      'Electrical Switchgear Placement',
      'Mechanical Shaft Pipe Rack Set',
    ],
    contractors: [
      'Great Lakes Rigging',
      'Blue Ridge Electric Systems',
      'Capital Mechanical Works',
    ],
  },
  {
    title:
      'Refresh permit-required confined space coordination for vaults, tanks, and below-grade utility structures',
    rationale:
      'Permit packages were late on 3 of the last 5 below-grade entries, and one manhole entry was started before atmospheric test results were attached to the permit. Under 29 CFR 1926.1203 and 1926.1204, construction employers directing entry need a written program, coordinated permits, and role clarity across all employers at the space.',
    expectedImpact:
      'Expected to reduce the chance of a severe low-frequency event while improving coordination between self-perform, utility, and specialty trade crews.',
    drivers: ['PTP Quality', 'Contractors Records'],
    activities: [
      'Stormwater Vault Entry',
      'Domestic Water Tank Interior Coating',
      'Manhole Channel Installation',
    ],
    contractors: [
      'Carolina Utility Services',
      'Heritage Industrial Coatings',
      'Atlas Confined Space Services',
    ],
  },
  {
    title:
      'Tighten steel joist bridging and connector sequencing before roof framing starts',
    rationale:
      'Open observations show joists being landed faster than bridging installation on the last two roof bays, and 38% of the ironworker crew roster turned over in the last month. Under 29 CFR 1926.757, 1926.760, and 1926.761, joist stability, fall protection, and training need to move together as the crew composition changes.',
    expectedImpact:
      'Expected to improve stability during one of the most unforgiving erection tasks, where sequencing mistakes can cascade across an entire bay.',
    drivers: ['Crew Stability', 'Worker Competency'],
    activities: [
      'Open-Web Joist Setting',
      'Bridging Installation',
      'Roof Deck Landing and Alignment',
    ],
    contractors: [
      'Northeast Steel Erectors',
      'Keystone Ironworks',
      'Summit Decking Solutions',
    ],
  },
  {
    title:
      'Resolve buried-utility verification before trenching and sawcut crews advance',
    rationale:
      'Utility conflict logs show 4 mark-out discrepancies in the last 21 days, including a fiber reroute that was not reflected in the field layout when sawcutting resumed. The exposure is less about paperwork than sequencing: locate verification, potholing, and operator briefings are not staying synchronized as civil work advances.',
    expectedImpact:
      'Expected to reduce utility strikes, unplanned outages, and emergency excavation changes that elevate both electrocution and caught-in/between exposure.',
    drivers: ['Project Type', 'Controls Validation'],
    activities: [
      'Potholing for Utility Verification',
      'Ductbank Sawcut and Demo',
      'Storm Structure Tie-In Excavation',
    ],
    contractors: [
      'Delta Utility Installers',
      'Frontier Earthworks',
      'Metro Sawcut & Coring',
    ],
  },
  {
    title:
      'Stage unitized curtain wall and prefab materials to cut congestion at active access points',
    rationale:
      'Site congestion scores spiked after curtain wall and prefab MEP deliveries overlapped with interior finish stocking, and 14 observations in 10 days involved blocked access, unsecured laydown, or workers rerouting through active equipment lanes. This is a logistics-control issue: the work itself is planned, but the site is no longer sized for the current delivery cadence.',
    expectedImpact:
      'Expected to reduce line-of-fire exposure, improve emergency access, and limit productivity losses caused by repeated rehandling of large components.',
    drivers: ['Site Congestion', 'Project Phase'],
    activities: [
      'Unitized Curtain Wall Staging',
      'Prefabricated Bathroom Pod Delivery',
      'Drywall Stocking by Hoist',
    ],
    contractors: [
      'Pacific Rim Glazing',
      'ModCore Interiors',
      'Harbor Trucking & Disposal',
    ],
  },
  {
    title:
      'Raise pre-task plan quality on high-risk work instead of accepting checkbox PTPs',
    rationale:
      'Only 5 of 13 high-risk PTPs this week described task-specific controls, red zones, or rescue steps; the rest repeated generic language despite active lifting, edge work, or energized tie-ins. Crew engagement is also weak — foremen closed 6 PTPs digitally before the full crew huddle occurred — so the planning step is not reliably shaping the workface.',
    expectedImpact:
      'Expected to improve frontline hazard recognition before work starts and reduce repeat observations on the same scopes.',
    drivers: ['PTP Quality', 'PTP Engagement'],
    activities: [
      'Tower Crane Pick Planning',
      'Energized Tie-In Preparation',
      'Leading Edge Deck Work',
    ],
    contractors: [
      'Northeast Steel Erectors',
      'Highline Electrical Constructors',
      'Urban Crane & Hoist',
    ],
  },
  {
    title:
      'Tighten subcontractor prequalification and first-week onboarding for high-turnover trades',
    rationale:
      "Three subcontractors added more than 20 new workers combined in the last 10 days, but only one submitted updated training rosters and recent OSHA history before mobilization. OSHA's construction recommended practices specifically call out safety considerations in subcontractor selection and multiemployer coordination; the current gap is at mobilization, not after incidents.",
    expectedImpact:
      'Expected to reduce avoidable first-week exposures, documentation gaps, and rework in how site rules are communicated across employers.',
    drivers: ['Contractors Records', 'New Worker Ratio'],
    activities: [
      'Drywall Crew Mobilization',
      'Site Concrete Finish Crew Add-On',
      'Weekend Electrical Shutdown Work',
    ],
    contractors: [
      'Monument Interiors',
      'Sunbelt Concrete Placers',
      'Blue Ridge Electric Systems',
    ],
  },
  {
    title:
      'Close the inspection-to-reinspection loop on repeat deficiencies within 48 hours',
    rationale:
      'Reinspection completion fell below trend for two consecutive weeks, and 8 of the 19 open items are repeat conditions on access, housekeeping, and incomplete barricades. The project is finding hazards, but corrective action closure is lagging behind the pace of work.',
    expectedImpact:
      'Expected to reduce repeat exposure and strengthen credibility of the inspection process with both field supervision and trade partners.',
    drivers: ['Reinspection Rate', 'Corrective Action Closure'],
    activities: [
      'Weekly Area Safety Inspection',
      'Hoist Landing Walkdown',
      'Facade Loading Bay Audit',
    ],
    contractors: [
      'Metro Waterproofing Group',
      'Harbor Trucking & Disposal',
      'Capital Air Balance & Sheet Metal',
    ],
  },
  {
    title:
      'Shift from mostly negative observations to balanced coaching with verified positive controls',
    rationale:
      'Observation volume is strong, but the positive observation ratio has dropped to 14% and crews are treating the process as enforcement rather than feedback. Large-project programs work better when observations catch controls that are working and then verify them during repeat tasks, not only when something is wrong.',
    expectedImpact:
      'Expected to improve participation, earlier hazard reporting, and durability of controls across repeated tasks and shifting crews.',
    drivers: ['Positive Observation Ratio', 'Safety Culture Index'],
    activities: [
      'Morning Huddle Walks',
      'Foreman Area Audits',
      'Weekend Shutdown Start-Up',
    ],
    contractors: [
      'Keystone Layout Services',
      'Capital Mechanical Works',
      'Pacific Rim Glazing',
    ],
  },
  {
    title:
      "Improve toolbox talk engagement with trade-specific, bilingual content tied to this week's high-risk work",
    rationale:
      'Digital sign-in is high, but engagement checks show only 52% of sampled workers could restate the key control from the last three toolbox talks, and the weakest recall was on mixed-language crews. The content is being delivered, but it is not landing at the point of work or connecting to the tasks starting that day.',
    expectedImpact:
      'Expected to strengthen worker understanding before mobilization, especially on fast-moving crews where verbal transfer of controls is critical.',
    drivers: ['Toolbox Engagement', 'Worker Competency'],
    activities: [
      'Pre-Shift Stretch and Huddle',
      'Weekend Shutdown Briefing',
      'Trade Foreman Coordination Meeting',
    ],
    contractors: [
      'Gulf Coast Mechanical',
      'Monument Interiors',
      'Frontier Earthworks',
    ],
  },
]

const PRIORITIES = [
  { level: 'critical', weight: 15, minRed: 25, maxRed: 35 },
  { level: 'high', weight: 30, minRed: 15, maxRed: 25 },
  { level: 'medium', weight: 35, minRed: 8, maxRed: 15 },
  { level: 'low', weight: 20, minRed: 5, maxRed: 8 },
]

function pickPriority() {
  const rand = rng.nextInt(1, 100)
  let sum = 0
  for (const p of PRIORITIES) {
    sum += p.weight
    if (rand <= sum) return p
  }
  return PRIORITIES[2]
}

function generateActivityExample(
  topic: (typeof TOPICS)[0],
  projectName: string,
): ActivityExample {
  const currentRisk = rng.pick(['critical', 'high', 'medium'] as const)
  const riskAfter =
    currentRisk === 'critical' ? rng.pick(['medium', 'low'] as const) : 'low'
  return {
    activityName: rng.pick(topic.activities),
    projectName,
    currentRisk,
    riskAfter,
    contractor: rng.pick(topic.contractors),
    floor: rng.pick([
      'Fl 1',
      'Fl 2',
      'Fl 3',
      'Basement',
      'Roof',
      'Site',
      'Ext',
    ]),
    explanation:
      'Implementation of this recommendation directly mitigates the primary hazard.',
  }
}

let recIdCounter = 4 // Start after the 3 hardcoded systemic ones

function generateRecommendation(
  scope: 'systemic' | 'region' | 'project',
  affectedProjects: string[],
  regionId?: string,
): Recommendation {
  const topic = rng.pick(TOPICS)
  const priorityObj = pickPriority()
  const riskReduction = rng.nextInt(priorityObj.minRed, priorityObj.maxRed)

  const activityExamples: ActivityExample[] = []
  const numExamples = rng.nextInt(1, 2)
  for (let i = 0; i < numExamples; i++) {
    const proj =
      scope === 'project' ? affectedProjects[0] : rng.pick(affectedProjects)
    activityExamples.push(generateActivityExample(topic, proj))
  }

  return {
    id: `rec-${recIdCounter++}`,
    title: topic.title,
    priority: priorityObj.level as any,
    rationale: topic.rationale,
    expectedImpact: topic.expectedImpact,
    affectedProjects,
    linkedDrivers: topic.drivers,
    scope,
    regionId,
    riskReduction,
    projectsAffected: scope === 'project' ? undefined : affectedProjects.length,
    contractorsAffected: scope === 'project' ? rng.nextInt(1, 4) : undefined,
    activityExamples,
  }
}

// 1. Keep the 3 best existing systemic recommendations
const generatedRecs: Recommendation[] = [
  {
    id: 'rec-1',
    title:
      'Re-establish edge protection and PFAS tie-off discipline on upper-floor slab perimeters',
    priority: 'critical',
    rationale:
      'The system flagged 6 fall-protection observations in the last 7 days on floors 18-22, including two crews working within 6 feet of unprotected edges after guardrail sections were removed for material landing. The issue is clustering around afternoon concrete patchback and layout work, and the exposure aligns with 29 CFR 1926.501 and 1926.502 requirements.',
    expectedImpact:
      'Addresses the leading fatal event in construction; BLS recorded 389 construction deaths from falls, slips, and trips in 2024.',
    affectedProjects: [
      'Court Square LIC',
      'Hudson Yards Tower C',
      'Midtown Atlanta Tower',
      'Chicago Union Station',
    ],
    linkedDrivers: ['Observation Severity', 'Open Observation Count'],
    scope: 'systemic',
    riskReduction: 18,
    projectsAffected: 42,
    activityExamples: [
      {
        activityName: 'Slab Edge Formwork Strip-Out',
        projectName: 'Court Square LIC',
        currentRisk: 'critical',
        riskAfter: 'medium',
        contractor: 'Empire City Concrete',
        floor: 'Fl 20',
        explanation:
          'Reinstating guardrails and requiring 100% tie-off during strip-out eliminates the unprotected edge exposure.',
      },
      {
        activityName: 'Embed Layout at Floor Perimeter',
        projectName: 'Hudson Yards Tower C',
        currentRisk: 'high',
        riskAfter: 'low',
        contractor: 'Keystone Layout Services',
        floor: 'Fl 18',
        explanation:
          'Moving layout work behind re-established guardrails removes the fall-to-below hazard entirely.',
      },
    ],
  },
  {
    id: 'rec-2',
    title:
      'Standardize ladder access and housekeeping in shafts, stairs, and above-ceiling rough-in zones',
    priority: 'high',
    rationale:
      'Nine ladder-use observations were logged in the last 14 days, and 41% of the electrical and mechanical rough-in manpower on the affected floors has been on site less than 30 days. Portable ladders are being used as work platforms in stair towers and above-ceiling zones, pointing to a training-and-access issue under 29 CFR 1926.1053.',
    expectedImpact:
      'Targets a persistent precursor to falls and strains during interior build-out, especially when crews are turning over quickly and workfronts change floor by floor.',
    affectedProjects: [
      'Brooklyn Navy Yard',
      'Austin Tech Campus',
      'Denver Union Station',
      'Seattle Waterfront',
    ],
    linkedDrivers: ['New Worker Ratio', 'Observation Frequency'],
    scope: 'systemic',
    riskReduction: 14,
    projectsAffected: 56,
    activityExamples: [
      {
        activityName: 'Stair Tower Conduit Rough-In',
        projectName: 'Brooklyn Navy Yard',
        currentRisk: 'high',
        riskAfter: 'low',
        contractor: 'Blue Ridge Electric Systems',
        floor: 'Fl 8',
        explanation:
          'Replacing portable ladders with fixed scaffold platforms in stair towers eliminates the primary fall and overexertion hazard.',
      },
      {
        activityName: 'Above-Ceiling Duct Branch Installation',
        projectName: 'Austin Tech Campus',
        currentRisk: 'high',
        riskAfter: 'medium',
        contractor: 'Capital Air Balance & Sheet Metal',
        floor: 'Fl 3',
        explanation:
          'Standardized access equipment and housekeeping checklists reduce trip and fall exposure in congested above-ceiling zones.',
      },
    ],
  },
  {
    id: 'rec-3',
    title:
      'Tighten supported scaffold inspections and front-edge protection before facade and masonry work starts',
    priority: 'high',
    rationale:
      'Three required scaffold inspections were missed this week, and 5 observations documented incomplete decking or excessive platform-to-face gaps after scaffold moves on the south elevation. The pattern is repeating after reconfiguration, tying directly to 29 CFR 1926.451 access and platform requirements and 1926.454 training expectations.',
    expectedImpact:
      'Expected to reduce exposure during exterior access work where small setup defects can quickly become serious fall or dropped-object events.',
    affectedProjects: [
      'Midtown Atlanta Tower',
      'Chicago Union Station',
      'Court Square LIC',
      'Phoenix Sky Harbor',
    ],
    linkedDrivers: ['Inspection Compliance', 'Reinspection Rate'],
    scope: 'systemic',
    riskReduction: 16,
    projectsAffected: 38,
    activityExamples: [
      {
        activityName: 'Brick Veneer Repair from Supported Scaffold',
        projectName: 'Midtown Atlanta Tower',
        currentRisk: 'critical',
        riskAfter: 'medium',
        contractor: 'Tri-State Masonry Restoration',
        floor: 'Ext',
        explanation:
          'Mandatory pre-start scaffold inspection and documented front-edge checks close the gap between reconfiguration and crew access.',
      },
      {
        activityName: 'Facade Sealant Replacement',
        projectName: 'Chicago Union Station',
        currentRisk: 'high',
        riskAfter: 'low',
        contractor: 'Atlantic Facade Access',
        floor: 'Ext',
        explanation:
          'Adding a competent-person sign-off after every scaffold move ensures platform integrity before work resumes.',
      },
    ],
  },
]

// 2. Generate 7 more systemic recommendations (Total 10)
const allProjectNames = PROJECT_REGION_MAP.map((p) => p.projectName)
for (let i = 0; i < 7; i++) {
  generatedRecs.push(generateRecommendation('systemic', allProjectNames))
}

// 3. Generate Region recommendations
// Each region gets a different slice of TOPICS to ensure variety across BUs
const regionCounts: Record<string, number> = {
  northeast: 6,
  mid_atlantic: 2,
  southeast: 4,
  midwest: 2,
  southwest: 6,
  west_coast: 4,
}

const regionIds = Object.keys(regionCounts)
// Build a per-region topic pool: each region starts at a different offset in the shuffled topic list
const shuffledTopicsForRegions = rng.shuffle([...TOPICS])
let regionTopicOffset = 0

Object.entries(regionCounts).forEach(([regionId, count]) => {
  const regionProjects = PROJECT_REGION_MAP.filter(
    (p) => p.regionId === regionId,
  ).map((p) => p.projectName)
  for (let i = 0; i < count; i++) {
    // Pick topic from the region's dedicated slice (wrapping around if needed)
    const topicIndex = (regionTopicOffset + i) % shuffledTopicsForRegions.length
    const topic = shuffledTopicsForRegions[topicIndex]
    const priorityObj = pickPriority()
    const riskReduction = rng.nextInt(priorityObj.minRed, priorityObj.maxRed)
    const activityExamples: ActivityExample[] = []
    const numExamples = rng.nextInt(1, 2)
    for (let j = 0; j < numExamples; j++) {
      activityExamples.push(
        generateActivityExample(topic, rng.pick(regionProjects)),
      )
    }
    generatedRecs.push({
      id: `rec-${recIdCounter++}`,
      title: topic.title,
      priority: priorityObj.level as any,
      rationale: topic.rationale,
      expectedImpact: topic.expectedImpact,
      affectedProjects: regionProjects,
      linkedDrivers: topic.drivers,
      scope: 'region',
      regionId,
      riskReduction,
      projectsAffected: regionProjects.length,
      activityExamples,
    })
  }
  regionTopicOffset += count // Advance offset so next region starts with different topics
})

// 4. Generate Project recommendations
// Instead of random distribution, generate based on actual project risk parameters

// Simplified parameter definitions to avoid circular dependency with RiskMatrix
const PARAM_DEFS = [
  { id: 'ptpQuality', label: 'PTP Quality', mapFromFactor: 'ptpQuality' },
  {
    id: 'ptpEngagement',
    label: 'PTP Engagement',
    mapFromFactor: 'ptpEngagement',
  },
  { id: 'jhaQuality', label: 'JHA Quality', mapFromFactor: 'documents' },
  {
    id: 'toolboxEngagement',
    label: 'Toolbox Engagement',
    mapFromFactor: 'ptpEngagement',
  },
  {
    id: 'controlsValidation',
    label: 'Controls Validation',
    mapFromFactor: 'inspections',
  },
  {
    id: 'ppeValidation',
    label: 'PPE Validation',
    mapFromFactor: 'inspections',
  },
  {
    id: 'sdsCompliance',
    label: 'SDS Compliance & Engagement',
    mapFromFactor: 'documents',
  },
  {
    id: 'observationFrequency',
    label: 'Observation Frequency',
    mapFromFactor: 'inspections',
  },
  {
    id: 'observationSeverity',
    label: 'Observation Severity',
    mapFromFactor: 'incidents',
  },
  {
    id: 'openObservationCount',
    label: 'Open Observation Count',
    mapFromFactor: 'incidents',
  },
  {
    id: 'positiveObservationRatio',
    label: 'Positive Observation Ratio',
    mapFromFactor: 'inspections',
  },
  {
    id: 'inspectionCompliance',
    label: 'Inspection Compliance',
    mapFromFactor: 'inspections',
  },
  {
    id: 'reinspectionRate',
    label: 'Reinspection Rate',
    mapFromFactor: 'inspections',
  },
  {
    id: 'workerCompetency',
    label: 'Worker Competency',
    mapFromFactor: 'workers',
  },
  { id: 'newWorkerRatio', label: 'New Worker Ratio', mapFromFactor: 'workers' },
  { id: 'fatigueRisk', label: 'Fatigue Risk', mapFromFactor: 'workers' },
  { id: 'crewStability', label: 'Crew Stability', mapFromFactor: 'workers' },
  {
    id: 'supervisionRatio',
    label: 'Supervision Ratio',
    mapFromFactor: 'workers',
  },
  {
    id: 'contractorHistory',
    label: 'Contractors Records',
    mapFromFactor: 'workers',
  },
  {
    id: 'contractorObservations',
    label: 'Contractors Observations & Inspections',
    mapFromFactor: 'inspections',
  },
  {
    id: 'openIncidents',
    label: 'Open Incident Count',
    mapFromFactor: 'incidents',
  },
  {
    id: 'incidentRecurrence',
    label: 'Incident Recurrence',
    mapFromFactor: 'incidents',
  },
  { id: 'nearMissRatio', label: 'Near-Miss Ratio', mapFromFactor: 'incidents' },
  {
    id: 'correctiveActionClosure',
    label: 'Corrective Action Closure',
    mapFromFactor: 'incidents',
  },
  { id: 'projectType', label: 'Project Type', mapFromFactor: 'weather' },
  { id: 'projectPhase', label: 'Project Phase', mapFromFactor: 'weather' },
  {
    id: 'weatherSeverity',
    label: 'Weather Severity',
    mapFromFactor: 'weather',
  },
  { id: 'siteCongestion', label: 'Site Congestion', mapFromFactor: 'weather' },
  {
    id: 'hazardousMaterials',
    label: 'Hazardous Materials',
    mapFromFactor: 'weather',
  },
  {
    id: 'heavyEquipmentDensity',
    label: 'Heavy Equipment Density',
    mapFromFactor: 'weather',
  },
  {
    id: 'safetyCultureIndex',
    label: 'Safety Culture Index',
    mapFromFactor: 'workers',
  },
]

const SEVERITY_ORDER: Record<FactorSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
}

// Group activities by project
const projectsMap = new Map<string, typeof ENRICHED_MOCK_RISK_AREAS>()
ENRICHED_MOCK_RISK_AREAS.forEach((area) => {
  if (!projectsMap.has(area.project)) {
    projectsMap.set(area.project, [])
  }
  projectsMap.get(area.project)!.push(area)
})

// Map parameter labels to TOPICS
const topicsByDriver = new Map<string, typeof TOPICS>()
TOPICS.forEach((topic) => {
  topic.drivers.forEach((driver) => {
    if (!topicsByDriver.has(driver)) {
      topicsByDriver.set(driver, [])
    }
    topicsByDriver.get(driver)!.push(topic)
  })
})

Array.from(projectsMap.entries()).forEach(([projectName, activities]) => {
  // Calculate parameter severities for this project
  const paramSeverities = PARAM_DEFS.map((param) => {
    let highCount = 0
    let totalWeight = 0
    let weightedSeveritySum = 0
    let dataCount = 0

    activities.forEach((a) => {
      const sev = a.riskFactorScores[
        param.mapFromFactor as keyof typeof a.riskFactorScores
      ] as FactorSeverity
      const isNoData = a.dataConfidence === 'low' && sev === 'none'
      if (!isNoData) {
        dataCount++
        if (sev === 'high') highCount++
        const weight = Math.max(10, a.riskScore)
        weightedSeveritySum += SEVERITY_ORDER[sev] * weight
        totalWeight += weight
      }
    })

    let severity: FactorSeverity = 'none'
    if (dataCount > 0) {
      const avgVal = weightedSeveritySum / totalWeight
      if (avgVal >= 2.5) severity = 'high'
      else if (avgVal >= 1.5) severity = 'medium'
      else if (avgVal >= 0.5) severity = 'low'
    }

    return { param, severity }
  })

  // Filter to high/medium and sort by severity
  const topParams = paramSeverities
    .filter((p) => p.severity === 'high' || p.severity === 'medium')
    .sort((a, b) => SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity])
    .slice(0, 3) // Take top 3

  // Generate recommendations for the top parameters
  const usedTopics = new Set<string>()
  topParams.forEach(({ param, severity }) => {
    const matchingTopics = topicsByDriver.get(param.label) || []
    const availableTopics = matchingTopics.filter(
      (t) => !usedTopics.has(t.title),
    )

    let topic: (typeof TOPICS)[0]
    if (availableTopics.length > 0) {
      topic = rng.pick(availableTopics)
    } else {
      // Fallback if no matching topic found or all used
      topic =
        rng.pick(TOPICS.filter((t) => !usedTopics.has(t.title))) ||
        rng.pick(TOPICS)
    }

    usedTopics.add(topic.title)

    const priorityObj = pickPriority()
    // Boost priority if severity is high
    const finalPriority = severity === 'high' ? 'critical' : priorityObj.level
    const riskReduction = rng.nextInt(priorityObj.minRed, priorityObj.maxRed)

    const activityExamples: ActivityExample[] = []
    const numExamples = rng.nextInt(1, 2)
    for (let j = 0; j < numExamples; j++) {
      activityExamples.push(generateActivityExample(topic, projectName))
    }

    generatedRecs.push({
      id: `rec-${recIdCounter++}`,
      title: topic.title,
      priority: finalPriority as any,
      rationale: topic.rationale,
      expectedImpact: topic.expectedImpact,
      affectedProjects: [projectName],
      linkedDrivers: [param.label], // Use the actual parameter that triggered it
      scope: 'project',
      riskReduction,
      projectsAffected: 1,
      contractorsAffected: rng.nextInt(1, 4),
      activityExamples,
    })
  })
})

// Sort all recommendations by riskReduction descending
generatedRecs.sort((a, b) => b.riskReduction - a.riskReduction)

export const MOCK_RECOMMENDATIONS: Recommendation[] = generatedRecs
